import { forwardRef } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import s from './RequestCard.module.scss';
import { ReactComponent as AngleRight } from '../../../Icons/AngleRight.svg';
// ... rest of imports
import { initiateCallByUser } from '../../../services';
import { ReactComponent as LocationIcon } from '../../../Icons/Location-Outlined.svg';
import { ReactComponent as PatientIcon } from '../../../Icons/Patient.svg';
import { ReactComponent as CentreVisitIcon } from '../../../Icons/CenterVisitIcon.svg';
import { ReactComponent as OpenDoorIcon } from '../../../Icons/Open-Door.svg';
import { ReactComponent as HashtagIcon } from '../../../Icons/hashtag.svg';
import { ReactComponent as LabIcon } from '../../../Icons/lab.svg';
import { ReactComponent as DoctorIcon } from '../../../Icons/doctor.svg';
import { ReactComponent as HomeVisitIcon } from '../../../Icons/HomeVisitIcon.svg';
import { ReactComponent as ClockFillIcon } from '../../../Icons/clock-fill.svg';
import SearchLogo from '../../../Images/GIF/Finding.gif';
import CartLogo from '../../../Icons/card-logo.svg';
import OrderLogo from '../../../Icons/order-logo.svg';
import { logEvent } from '../../../../../../utils/logEvent';

const ORDER_CONFIRMED_TYPES = [
  'partnerConfirmation',
  'orderUpdatePartner',
  'partialReportGenerated',
  'reportGenerated',
  'partnerConfirmed',
  'orderUpdate',
  'delivery',
];

const CARD_STYLE_CONFIG = {
  digitisationDelayed: {
    barClass: s.barQuery,
    label: 'Action Required',
  },
  copay: {
    barClass: s.barQuery,
    label: 'Payment Pending',
  },
  sampleProcessing: {
    barClass: s.barProcess,
    label: 'Sample Processing',
  },
  samplePickUp: {
    barClass: s.barSuccess,
    label: 'Sample Pickup',
  },
  orderPlaced: {
    barClass: s.barProcess,
    label: 'Order Placed',
  },

  // Success (Green)
  // Success (Green)
  partnerConfirmation: {
    barClass: s.barSuccess,
    label: 'Confirmed',
  },
  cashlessLetterGenerated: {
    barClass: s.barProcess,
    label: 'Letter Ready',
  },
  availableCart: {
    barClass: s.barProcess,
    label: 'Ready to book',
  },
  // New Mappings
  digitisationRejected: { barClass: s.barQuery, label: 'Action Required' },
  digitisationPending: {
    barClass: s.barProcess,
    label: 'Digitisation in process',
  },
  LookingForPartner: { barClass: s.barQuery, label: 'Searching' },
  fetchingPartner: { barClass: s.barQuery, label: 'Searching' },
  partnerNotFound: { barClass: s.barProcess, label: 'Partner Not Found' },
  partnerFound: { barClass: s.barProcess, label: 'Partner Found' },
  partnerConfirmed: { barClass: s.barSuccess, label: 'Confirmed' },
  orderUpdate: { barClass: s.barSuccess, label: 'Order Update' },
  orderUpdatePartner: { barClass: s.barSuccess, label: 'Order Update' },
  orderCancelled: { barClass: s.barQuery, label: 'Cancelled' }, // Assuming Red for Cancelled
  delivery: { barClass: s.barSuccess, label: 'Out for Delivery' },
  partialReportGenerated: { barClass: s.barSuccess, label: 'Report Generated' },
  reportGenerated: { barClass: s.barSuccess, label: 'Report Generated' },
  orderUpdateReschedule: { barClass: s.barProcess, label: 'Rescheduled' },
};

const renderInfoSection = (card) => {
  switch (card.cardType) {
    case 'digitisationDelayed':
    case 'digitisationRejected':
      return (
        <>
          <div className={s.infoHeader}>
            <span className={s.infoTitle}>{card.title}</span>
            <AngleRight />
          </div>
          <div className={s.infoStack}>
            <p className={s.reasonText}>
              <strong>Reason: </strong> {card.infoText || card.reason}
            </p>
          </div>
        </>
      );

    case 'digitisationPending':
      return (
        <>
          <div className={s.infoHeader}>
            <span className={s.infoTitle}>{card.title}</span>
            <AngleRight />
          </div>
          <div className={s.infoStack}>
            {card.digitisationId && (
              <p className={s.infoText}>
                <HashtagIcon />
                Digitisation ID: {card.digitisationId}
              </p>
            )}
            <p className={s.infoText}>{card.infoText}</p>
          </div>
        </>
      );

    case 'LookingForPartner':
    case 'fetchingPartner':
      return (
        <>
          <div className={s.infoHeader}>
            <span className={s.infoTitle}>{card.title}</span>
            <AngleRight />
          </div>
          <div className={`${s.infoStack} ${s.compactGap}`}>
            {/* Patient */}
            <p className={s.patientRow}>
              <PatientIcon />
              <span className={s.rowText}>{card.patientName}</span>
            </p>
            {/* Lab Name (hardcoded as Pathology per req) */}
            <p className={s.locationRow}>
              <LabIcon />
              <span className={s.rowText}>{card.testsTypeName}</span>
            </p>
            {/* Request ID */}
            <p className={s.infoText}>
              <HashtagIcon />
              Request ID: {card.requestId || card.digitisationId}
            </p>
          </div>
        </>
      );

    case 'availableCart':
    case 'partnerNotFound':
    case 'partnerFound':
      return (
        <>
          <div className={s.infoHeader}>
            <span className={s.infoTitle}>{card.title}</span>
            <AngleRight />
          </div>

          <div className={`${s.infoStack} ${s.compactGap}`}>
            {/* Request ID */}
            {(card.requestId || card.digitisationId) && (
              <p className={s.infoText}>
                <HashtagIcon />
                Request ID: {card.requestId ?? card.digitisationId}
              </p>
            )}
            {/* Doctor / Consultation - optional */}
            {card.doctorName && card.title !== 'Place Order' && (
              <p className={s.infoText}>
                <DoctorIcon />
                {card.doctorName}
              </p>
            )}
            {/* Patient */}
            <p className={s.patientRow}>
              <PatientIcon />
              <span className={s.rowText}>{card.patientName}</span>
            </p>
            {/* Test Type */}
            <p
              className={s.infoText}
              onClick={(e) => {
                e.stopPropagation();

                card?.onTestClick(card);
              }}
            >
              <LabIcon />
              {card.testsTypeName || 'Pathology'}{' '}
              {card.testCount > 0 && (
                <span className={s.tests}>({card.testCount} Tests)</span>
              )}
            </p>
          </div>
        </>
      );

    case 'partnerConfirmation':
    case 'orderUpdatePartner':
    case 'orderPlaced':
    case 'samplePickUp':
    case 'sampleProcessing':
    case 'partialReportGenerated':
    case 'copay':
    case 'reportGenerated':
    case 'partnerConfirmed':
    case 'orderUpdate':
    case 'orderCancelled':
    case 'delivery':
      return (
        <>
          <div className={s.infoHeader}>
            <span className={s.infoTitle}>{card.title}</span>
            <AngleRight />
          </div>
          <div className={`${s.infoStack} ${s.compactGap}`}>
            {/* Location */}
            {(card.partnerName || card.labName) && (
              <p className={s.locationRow}>
                <LocationIcon />
                <span className={s.rowText}>
                  {card.partnerName || card.labName}
                </span>
              </p>
            )}

            {/* Patient */}
            {card.patientName && (
              <p className={s.patientRow}>
                <PatientIcon />
                <span className={s.rowText}>{card.patientName}</span>
              </p>
            )}

            {/* Visit Type + Test Count */}
            <p className={s.visitTypeRow}>
              {card.isCenterVisit ? <CentreVisitIcon /> : <LabIcon />}
              <span className={s.rowText}>
                {/* Logic: if Labname is Pathology then Center Visit text otherwise Home visit */}
                {card.isCenterVisit ? 'Centre Visit' : 'Home Visit'}{' '}
                {card.testCount > 0 && (
                  <strong
                    onClick={(e) => {
                      e.stopPropagation();
                      card?.onTestClick(card);
                    }}
                  >
                    ({card.testCount} Tests)
                  </strong>
                )}
              </span>
            </p>
            {/* Request ID */}
            {(card?.requestId || card.digitisationId) && (
              <p className={s.infoText}>
                <HashtagIcon />
                Request ID: {card.requestId || card.digitisationId}
              </p>
            )}
          </div>
        </>
      );

    case 'cashlessLetterGenerated':
    case 'orderUpdateReschedule':
      return (
        <>
          <div className={s.infoHeader}>
            <span className={s.infoTitle}>{card.title}</span>
            <AngleRight />
          </div>
          <div className={`${s.infoStack} ${s.compactGap}`}>
            {/* Location */}
            <p className={s.locationRow}>
              <LocationIcon />
              <span className={s.rowText}>{card.partnerName}</span>
            </p>

            {/* Patient */}
            <p className={s.patientRow}>
              <PatientIcon />
              <span className={s.rowText}>{card.patientName}</span>
            </p>

            {/* Visit Type */}
            <p className={s.visitTypeRow}>
              {card?.isCenterVisit ? <CentreVisitIcon /> : <LabIcon />}
              <span className={s.rowText}>
                {card.isCenterVisit ? 'Centre Visit' : 'Home Visit'}{' '}
                {card.testCount > 0 && (
                  <strong>({card.testCount} Tests)</strong>
                )}
              </span>
            </p>
          </div>
        </>
      );
    default:
      return (
        <div className={s.infoStack}>
          <p className={s.infoText}>{card.infoText}</p>
        </div>
      );
  }
};

const getFallbackLogo = (cardType) => {
  if (cardType === 'LookingForPartner' || cardType === 'fetchingPartner') {
    return SearchLogo;
  }
  if (
    cardType === 'availableCart' ||
    cardType === 'partnerFound' ||
    cardType === 'partnerNotFound'
  ) {
    return CartLogo;
  }
  return OrderLogo;
};

const RequestCard = forwardRef((props, ref) => {
  const history = useHistory();
  const authToken = useSelector((state) => state.user.authToken);
  const {
    cardType,
    title,
    status,
    infoText,
    digitisationId,
    uploadedOn,
    collectionDate,
    slot,
    index,
    orderId,
    cartId,
    isConciergeBooking,
    partnerLogoUrl,
    testsType,
    isScanNpay,
    isScanAndPayFlow = false,
    scanAndPayQrId,
    scanAndPayPartnerId,
    scanAndPayCenterId,
    requestId,
    addressId,
  } = props;

  const loginState = useSelector((state) => state.app?.loginState);

  const statusUI =
    CARD_STYLE_CONFIG[cardType] || CARD_STYLE_CONFIG['digitisationDelayed'];

  const handleCardClick = () => {
    const eventName = isScanAndPayFlow
      ? 'Scan QR Your Request’s card clicked'
      : 'Your Request’s card clicked';
    logEvent(eventName, {
      cardStatus: cardType,
      orderId,
      digitisationId,
      cartId,
      cartType: testsType,
      sequenceOfCard: index + 1,
      ...(isScanAndPayFlow && {
        qrId: scanAndPayQrId,
        partnerId: scanAndPayPartnerId,
        centerId: scanAndPayCenterId,
        isLoggedIn: loginState === 'LOGGED_IN',
        entrySource: 'scan_and_pay',
      }),
    });

    if (props.onCardClick) {
      props.onCardClick({
        cardType,
        digitisationId,
        orderId,
        cartId,
        uploadedOn,
        collectionDate,
        slot,
      });
      return;
    }
    if (
      isScanAndPayFlow &&
      [
        'availableCart',
        'digitisationPending',
        'cashlessLetterGenerated',
      ].includes(cardType)
    ) {
      history.push('/lab-test/order', { digitisationId, cartId });
    } else {
      if (cardType === 'availableCart') {
        history.push('/lab-test/lab-select', {
          cartId,
        });
      } else if (cardType === 'LookingForPartner') {
        history.push('/lab-test/lab-select', {
          cartId,
          requestId,
          addressId,
        });
      } else {
        history.push('/lab-test/order-status', {
          orderId,
          digitisationId,
          cartId,
        });
      }
    }
  };

  const handleHelpClick = (e) => {
    e.stopPropagation();
    if (orderId) {
      initiateCallByUser(authToken, orderId)
        .then((res) => {})
        .catch((err) => {
          console.error('Call initiation failed:', err);
        });
    }
  };

  return (
    <article
      ref={ref}
      data-index={index}
      className={`${s.card} ${statusUI.barClass}`}
      onClick={handleCardClick}
    >
      {/* TOP BAR */}
      <div className={s.topStrip}>
        {[
          'partnerConfirmation',
          'orderUpdateReschedule',
          'rescheduled',
          'samplePickUp',
        ].includes(cardType) && (
          <>
            {!cardType === 'samplePickUp' && (
              <div className={s.topLeft}>
                {/* Logic: Centre Visit -> OpenDoor, Home Visit -> HomeVisit (House) */}
                {props.isCenterVisit ? (
                  <OpenDoorIcon className={s.topLeftIcon} />
                ) : (
                  <HomeVisitIcon className={s.topLeftIcon} />
                )}
              </div>
            )}
            <span className={s.topLeftText}>{props.slotDateTime}</span>
          </>
        )}
        <div className={s.statusPill}>
          {statusUI.label === 'Searching' ? (
            <ClockFillIcon className={s.pillIcon} />
          ) : (
            <CheckCircleIcon className={s.pillIcon} />
          )}
          <span className={s.pillText}>{statusUI.label}</span>
        </div>
      </div>

      <div className={s.content}>
        {/* Left icon */}
        <img
          className={s.infoIcon}
          src={partnerLogoUrl || getFallbackLogo(cardType)}
          alt=""
        />

        {/* Right content */}
        <div className={s.info}>{renderInfoSection(props)}</div>
      </div>
      {ORDER_CONFIRMED_TYPES.includes(cardType) &&
        props.showEmergencyButtonCall && (
          <div className={s.helpFooter} onClick={handleHelpClick}>
            Need Help?
          </div>
        )}
    </article>
  );
});

RequestCard.displayName = 'RequestCard';
export default RequestCard;
